/**
 * submitOrder pour /b/[slug] (événement). Gère :
 *  - free order
 *  - free_with_promo
 *  - PayPal (redirect /payment)
 *  - Stripe (confirmCardPayment + redirect /success)
 *  - état processingStep / progress / orderError dans le modal
 */
import { eventsApi } from "~/services/api/events";
import type { CreateOrderPayload, TicketType } from "~/types/event";
import { extractApiError } from "~/utils/apiError";
import { commandeParCarte } from "~/utils/commandeParCarte";
import { parametresIntegration } from "~/utils/embed";

interface PendingOrderResult {
  order_id: string;
  payment_intent_id: string;
  client_secret: string;
  stripe_account_id?: string;
}

interface CheckoutHandle {
  confirmCardPayment: () => Promise<{ error?: unknown }>;
  isInitialized: { value: boolean };
  /** Champ carte RÉELLEMENT monté (≠ SDK chargé). */
  cardMounted: { value: boolean };
}

export interface UseEventOrderSubmitDeps {
  slug: Ref<string>;
  isPreview: Ref<boolean>;
  isProcessing: Ref<boolean>;
  needsPayment: Ref<boolean>;
  selectedProvider: Ref<"stripe" | "paypal">;
  checkout: CheckoutHandle;
  /**
   * Instance PayPal — utilisée quand `usesPaypalCard.value === true`
   * (preferred=paypal ou PayPal-only). Le SDK PayPal gère lui-même la
   * création de l'order via `createPaymentIntent` du useCheckout PayPal
   * et la redirection après capture — donc on appelle juste
   * `paypalCheckout.confirmCardPayment()` au lieu de Stripe.
   */
  paypalCheckout?: CheckoutHandle;
  usesPaypalCard?: Ref<boolean>;
  orderCompleted: Ref<boolean>;
  processingStep: Ref<"creating" | "paying" | "confirming" | "success" | "error">;
  processingProgress: Ref<number>;
  orderError: Ref<string | null>;
  orderErrorTechnical: Ref<boolean>;
  promoApplied: Ref<{ code: string } | null>;
  buyerInfo: Ref<{ firstName: string; lastName: string; email: string; phone: string }>;
  calculations: { finalAmount: { value: number }; amountWithFees: { value: number } };
  setPendingOrderResult: (r: PendingOrderResult) => void;
  /** La commande est payée ou confiée à une autre page : ne plus l'annuler
   *  à l'abandon (cf. `abandonPendingOrder` des providers). */
  clearPendingOrders?: () => void;
  /** Paiement refusé puis fermé par l'acheteur : la place est rendue TOUT DE
   *  SUITE (Karim 2026-09-06), pas au prochain cron. */
  abandonPendingOrder?: (opts?: { force?: boolean }) => Promise<void>;
  clearCheckoutSession: () => void;
  buildOrderPayload: () => CreateOrderPayload;
  startProgress: (target: number, duration?: number) => void;
  stopProgress: () => void;
  /**
   * Pre-flight disponibilité (dernier filet avant création de commande) :
   * tarifs courants + résolveur de places restantes. Optionnels — si absents,
   * on s'appuie sur le backend (autorité finale). Karim 2026-06-23.
   */
  ticketTypes?: Ref<TicketType[]>;
  getRemainingForDisplay?: (tt: TicketType) => number | null;
}

export function useEventOrderSubmit(deps: UseEventOrderSubmitDeps) {
  const { t } = useI18n();
  // Widget chez l'association (`?embed=1&host=…`) : les pages suivantes du
  // tunnel (paiement PayPal, succès) le restent.
  const route = useRoute();
  function setOrderError(e: unknown) {
    const info = extractApiError(e);
    deps.orderError.value = info.message;
    deps.orderErrorTechnical.value = info.isTechnical;
    if (info.isTechnical) console.error("[Order] Technical error:", e);
  }

  function clearOrderError() {
    deps.orderError.value = null;
    deps.orderErrorTechnical.value = false;
  }

  function showOrderErrorInModal(e: unknown) {
    setOrderError(e);
    deps.stopProgress();
    deps.processingStep.value = "error";
  }

  function dismissProcessingError() {
    deps.isProcessing.value = false;
    deps.processingStep.value = "creating";
    deps.processingProgress.value = 0;
    // L'acheteur referme l'erreur de paiement : sa réservation est libérée
    // sur-le-champ. S'il réessaie, une commande NEUVE est créée (submit
    // rappelle createOrder) — sans ça, l'ancienne bloquait une deuxième place.
    // `force` : abandon EXPLICITE (l'acheteuse referme l'erreur de paiement).
    // La feuille de paiement est fermée, la place doit repartir tout de suite —
    // contrairement à l'abandon automatique, bloqué pendant qu'un paiement est
    // en vol (cf. `paiementEnVol`, incident Apple Pay du 2026-09-11).
    void deps.abandonPendingOrder?.({ force: true });
  }

  function buyerEmail() { return deps.buyerInfo.value.email; }
  function buyerFullName() { return `${deps.buyerInfo.value.firstName} ${deps.buyerInfo.value.lastName}`.trim(); }

  /** Garde « systèmes de paiement prêts » (PayPal CardFields OU Stripe
   *  selon le path). false = erreur posée, la soumission doit s'arrêter. */
  function paymentSystemsReady(): boolean {
    if (!deps.needsPayment.value) return true;
    const usesPaypal = deps.usesPaypalCard?.value === true;
    if (usesPaypal && deps.paypalCheckout && !deps.paypalCheckout.cardMounted.value) {
      deps.orderError.value = t("billetterie.useEventOrderSubmit.paypalPasPret");
      deps.orderErrorTechnical.value = true;
      return false;
    }
    if (!usesPaypal && !deps.checkout.cardMounted.value) {
      deps.orderError.value = t("billetterie.useEventOrderSubmit.paiementPasPret");
      deps.orderErrorTechnical.value = true;
      return false;
    }
    return true;
  }

  /** Pre-flight disponibilité (dernier filet client) : si un tarif s'est
   *  rempli depuis la sélection (le socket rafraîchit `remaining`), on
   *  bloque AVANT de créer la commande, avec un message métier clair plutôt
   *  que d'attendre le 409 backend. Fail-open : si les deps de dispo sont
   *  absentes, on laisse le backend trancher (autorité finale). Karim 2026-06-23. */
  function availabilityPreflightOk(
    payload: ReturnType<UseEventOrderSubmitDeps["buildOrderPayload"]>,
  ): boolean {
    if (!deps.ticketTypes || !deps.getRemainingForDisplay) return true;
    for (const line of payload.tickets) {
      if (!line.quantity || line.quantity <= 0) continue;
      const tt = deps.ticketTypes.value.find((t) => t.id === line.ticket_type_id);
      if (!tt) continue;
      const remaining = deps.getRemainingForDisplay(tt);
      if (remaining !== null && line.quantity > remaining) {
        deps.stopProgress();
        deps.processingStep.value = "error";
        deps.orderError.value =
          remaining <= 0
            ? t("billetterie.useEventOrderSubmit.tarifComplet", { nom: tt.name })
            : t("billetterie.useEventOrderSubmit.placesRestantesErreur", { nom: tt.name }, remaining);
        deps.orderErrorTechnical.value = false;
        deps.isProcessing.value = false;
        return false;
      }
    }
    return true;
  }

  /** Séquence de succès commune : fin de progress + état success +
   *  nettoyage de la session checkout. */
  function markOrderSuccess() {
    deps.stopProgress();
    deps.processingProgress.value = 100;
    deps.processingStep.value = "success";
    deps.orderCompleted.value = true;
    deps.clearCheckoutSession();
    // Payée : plus jamais annulable par un départ de page.
    deps.clearPendingOrders?.();
  }

  async function submitOrder() {
    if (deps.isProcessing.value) return;
    // Check ready : si PayPal CardFields → on regarde paypalCheckout, sinon Stripe.
    if (!paymentSystemsReady()) return;

    deps.isProcessing.value = true;
    deps.processingStep.value = "creating";
    deps.processingProgress.value = 0;
    clearOrderError();
    let redirecting = false;
    deps.startProgress(25);

    try {
      const isTrulyFree = !deps.needsPayment.value && !deps.promoApplied.value;
      const payload = deps.buildOrderPayload();

      if (!availabilityPreflightOk(payload)) return;

      if (isTrulyFree) {
        const result = await eventsApi.createFreeOrder(deps.slug.value, payload, deps.isPreview.value);
        markOrderSuccess();
        await new Promise((r) => setTimeout(r, 1500));
        redirecting = true;
        await navigateTo({
          path: `/b/${deps.slug.value}/success`,
          query: { order_id: result.order_id, email: buyerEmail(), ...parametresIntegration(route.query) },
        });
        return;
      }

      // Path PayPal CardFields (preferred=paypal) : géré inline par le SDK
      // PayPal (createPaymentIntent + onCapture du useCheckout PayPal). Pas
      // de createOrder backend séparé ici — il est appelé par createPaymentIntent.
      if (deps.usesPaypalCard?.value && deps.paypalCheckout) {
        deps.processingStep.value = "paying";
        deps.startProgress(60);
        const { error } = await deps.paypalCheckout.confirmCardPayment();
        if (error) {
          showOrderErrorInModal(error);
          return;
        }
        // Le SDK PayPal redirige lui-même vers successUrl après capture.
        markOrderSuccess();
        redirecting = true;
        return;
      }

      // Carte Stripe : la commande part TOUJOURS en carte, même si l'acheteur
      // avait ouvert PayPal avant (cf. commandeParCarte).
      const orderResult = await eventsApi.createOrder(
        deps.slug.value,
        commandeParCarte(payload),
        deps.isPreview.value,
      );

      if (orderResult.free_with_promo) {
        markOrderSuccess();
        await new Promise((r) => setTimeout(r, 1500));
        redirecting = true;
        await navigateTo({
          path: `/b/${deps.slug.value}/success`,
          query: { order_id: orderResult.order_id, email: buyerEmail(), ...parametresIntegration(route.query) },
        });
        return;
      }

      const orderProvider = orderResult.payment_provider as "stripe" | "paypal" | undefined;

      if (orderProvider === "paypal") {
        markOrderSuccess();
        // La page de paiement PayPal reprend la commande : c'est elle qui la
        // libère si l'acheteur repart de là.
        deps.clearPendingOrders?.();
        redirecting = true;
        await navigateTo({
          path: `/b/${deps.slug.value}/payment`,
          query: {
            order_id: orderResult.order_id,
            paypal_order_id: orderResult.paypal_order_id,
            paypal_merchant_id: orderResult.paypal_merchant_id,
            // Montant DÉBITÉ, tel que fixé par le serveur (billets, don à
            // l'association, frais, soutien Donoor). `finalAmount` ne portait
            // que les billets : la page PayPal affichait moins que ce que
            // l'Order encaissait.
            amount: String(
              orderResult.amount_breakdown?.total ??
                deps.calculations.amountWithFees.value,
            ),
            email: buyerEmail(),
            name: buyerFullName(),
            payment_provider: "paypal",
            ...parametresIntegration(route.query),
          },
        });
        return;
      }

      deps.setPendingOrderResult({
        order_id: orderResult.order_id,
        payment_intent_id: orderResult.payment_intent_id ?? "",
        client_secret: orderResult.client_secret ?? "",
        stripe_account_id: orderResult.stripe_account_id,
      });

      deps.processingStep.value = "paying";
      deps.startProgress(60);

      const { error } = await deps.checkout.confirmCardPayment();

      if (error) {
        showOrderErrorInModal(error);
        return;
      }

      deps.processingStep.value = "confirming";
      deps.startProgress(90);

      try {
        await eventsApi.confirmOrder(orderResult.order_id, orderResult.payment_intent_id);
      } catch {
        // ignore — webhook will confirm
      }

      markOrderSuccess();
      redirecting = true;
    } catch (e: unknown) {
      showOrderErrorInModal(e);
    } finally {
      if (!redirecting && (deps.processingStep.value as string) !== "error") {
        deps.isProcessing.value = false;
      }
    }
  }

  return { submitOrder, setOrderError, clearOrderError, showOrderErrorInModal, dismissProcessingError };
}
